import i18next, { i18n as I18nInstance, InitOptions } from 'i18next';
import LocizeBackend from 'i18next-locize-backend';
import { initReactI18next } from 'react-i18next';

import { NAMESPACES, tenantNamespace } from './namespaces';

const isDev = process.env.NODE_ENV === 'development';
const isServer = typeof window === 'undefined';

const LOCIZE_PROJECT_ID = process.env.NEXT_PUBLIC_LOCIZE_PROJECT_ID ?? '';
const LOCIZE_VERSION = process.env.NEXT_PUBLIC_LOCIZE_VERSION ?? 'latest';

const LOCIZE_API_KEY = isDev ? process.env.NEXT_PUBLIC_LOCIZE_API_KEY : undefined;

const RELOAD_INTERVAL_MS = Number(process.env.NEXT_PUBLIC_LOCIZE_RELOAD_INTERVAL_MS ?? (isDev ? '300000' : '0'));

let i18nSingleton: I18nInstance | null = null;

// tenantId is fetched at runtime from /api/config (see provider.tsx) to bypass
// Next.js build-time static inlining of NEXT_PUBLIC_* vars.
export function getI18n(defaultLocale = 'en-GB', tenantId?: string): I18nInstance {
  if (i18nSingleton) return i18nSingleton;

  const instance = i18next.createInstance();

  if (!isServer) {
    instance.use(LocizeBackend);
  }

  instance.use(initReactI18next);

  const options: InitOptions = {
    lng: defaultLocale,
    fallbackLng: 'en-GB',
    defaultNS: NAMESPACES.COMMON,
    ns: [NAMESPACES.USECASE, NAMESPACES.DATASET, NAMESPACES.GOVERNANCE_EVALUATION],
    interpolation: {
      escapeValue: false,
    },
    load: 'currentOnly',
    react: {
      useSuspense: true,
    },
    backend: {
      projectId: tenantId || LOCIZE_PROJECT_ID,
      apiKey: LOCIZE_API_KEY,
      version: LOCIZE_VERSION,
      storageExpiration: 0,
      noCache: true,
      reloadInterval: RELOAD_INTERVAL_MS || false,
    },
    // Auto-create missing keys in Locize during development.
    // Locize returns 412 if the key already exists — silence it with updateMissing: false.
    saveMissing: isDev && !!LOCIZE_API_KEY && !isServer,
    updateMissing: false,
  };

  instance.init(options);

  i18nSingleton = instance;
  return instance;
}

// Multi-tenant support
export async function loadTenantOverrides(tenantId: string): Promise<string> {
  const instance = getI18n();
  const ns = tenantNamespace(tenantId);

  if (!instance.hasLoadedNamespace(ns)) {
    await instance.loadNamespaces(ns);
  }

  return ns;
}
